import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { type WorkflowTemplate } from "~/api/types";
import { Alert } from "~/atoms/alert";
import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { type ApplyTemplateRow } from "~/hooks/useApplyTemplateRows";
import {
  useWorkflowDatesEditor,
  type WorkflowDates,
} from "~/hooks/useWorkflowDatesEditor";
import { StepProgress } from "~/molecules/StepProgress";
import {
  DueDateGrid,
  dueDateGridDialogClassName,
} from "~/organisms/DueDateGrid";
import { ApplyTemplateDescriptionsStep } from "./ApplyTemplateDescriptionsStep";

/** Steps of the apply chain, in order. The description step is skipped when nothing would be lost. */
type WizardStep = "descriptions" | "dates" | "confirm";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateName: string;
  templateDescription: string | null;
  steps: WorkflowTemplate["steps"];
  rows: ApplyTemplateRow[];
  onApply: (
    dates: Record<number, WorkflowDates>,
    keepDescriptionIds: number[],
  ) => void;
  isApplying: boolean;
}

export function ApplyTemplateDialog({ open, ...content }: Props) {
  return (
    <Dialog open={open} onOpenChange={content.onOpenChange}>
      {open && <ApplyTemplateDialogContent {...content} />}
    </Dialog>
  );
}

function ApplyTemplateDialogContent({
  templateName,
  templateDescription,
  steps,
  rows,
  onApply,
  isApplying,
  onOpenChange,
}: Omit<Props, "open">) {
  const { t } = useTranslation();

  const sortedSteps = [...steps].sort((a, b) => a.order - b.order);

  /** Rows that would silently lose a description they already have. */
  const conflicts = rows.filter(
    (row) =>
      (row.currentDescription ?? "").trim() !== "" &&
      row.currentDescription !== templateDescription,
  );

  const hasDescriptionStep = conflicts.length > 0;
  const wizardSteps: WizardStep[] = hasDescriptionStep
    ? ["descriptions", "dates", "confirm"]
    : ["dates", "confirm"];

  const [step, setStep] = useState<WizardStep>(
    hasDescriptionStep ? "descriptions" : "dates",
  );
  const [keepDescriptionIds, setKeepDescriptionIds] = useState(
    () => new Set(conflicts.map((row) => row.id)),
  );

  const datesEditor = useWorkflowDatesEditor(rows, sortedSteps.length);

  const stepDescription = {
    descriptions: t(
      ($) => $.workflowTemplates.applyDialog.descriptions.description,
      { count: conflicts.length, total: rows.length },
    ),
    dates: t(($) => $.workflowTemplates.applyDatesDialog.description),
    confirm: t(($) => $.workflowTemplates.applyDatesDialog.confirmDescription),
  }[step];

  return (
    <DialogContent className={dueDateGridDialogClassName}>
      <DialogHeader>
        <DialogTitle className="flex flex-col gap-1">
          <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {t(($) => $.workflowTemplates.applyDialog.title)}
          </span>
          {templateName}
        </DialogTitle>
        <StepProgress
          size="sm"
          currentIndex={wizardSteps.indexOf(step)}
          steps={wizardSteps.map((name) => ({
            title: t(($) => $.workflowTemplates.applyDialog.steps[name]),
          }))}
        />
        {step === "descriptions" ? (
          <Alert variant="info">
            <Info />
            <DialogDescription>{stepDescription}</DialogDescription>
          </Alert>
        ) : (
          <DialogDescription>{stepDescription}</DialogDescription>
        )}
      </DialogHeader>

      {step === "descriptions" ? (
        <ApplyTemplateDescriptionsStep
          templateDescription={templateDescription}
          conflicts={conflicts}
          keepDescriptionIds={keepDescriptionIds}
          onKeepDescriptionIdsChange={setKeepDescriptionIds}
        />
      ) : (
        <DueDateGrid
          rows={rows}
          steps={sortedSteps}
          readOnly={step === "confirm"}
          datesEditor={datesEditor}
        />
      )}

      <DialogFooter>
        {step === "descriptions" && (
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t(($) => $.commons.buttons.cancel)}
            </Button>
            <Button onClick={() => setStep("dates")}>
              {t(($) => $.commons.buttons.continue)}
            </Button>
          </>
        )}

        {step === "dates" && (
          <>
            <Button
              variant="outline"
              onClick={() =>
                hasDescriptionStep
                  ? setStep("descriptions")
                  : onOpenChange(false)
              }
            >
              {hasDescriptionStep
                ? t(($) => $.commons.buttons.back)
                : t(($) => $.commons.buttons.cancel)}
            </Button>
            <Button
              onClick={() => setStep("confirm")}
              disabled={datesEditor.hasErrors}
            >
              {t(($) => $.workflowTemplates.applyDatesDialog.confirmDates)}
            </Button>
          </>
        )}

        {step === "confirm" && (
          <>
            <Button
              variant="outline"
              onClick={() => setStep("dates")}
              disabled={isApplying}
            >
              {t(($) => $.commons.buttons.back)}
            </Button>
            <Button
              onClick={() =>
                onApply(Object.fromEntries(datesEditor.dates), [
                  ...keepDescriptionIds,
                ])
              }
              disabled={isApplying}
            >
              {isApplying
                ? t(($) => $.workflowTemplates.applying)
                : t(($) => $.workflowTemplates.applyDatesDialog.apply)}
            </Button>
          </>
        )}
      </DialogFooter>
    </DialogContent>
  );
}
